import React from 'react';

import styled from 'styled-components';

import A from '@adsk/alloy-react-a';
import Badge from '@adsk/alloy-react-badge';
import Divider from '@adsk/alloy-react-divider';
import { useLocale } from '@adsk/alloy-react-locale';
import theme from '@adsk/alloy-react-theme';
import { OverflowTooltip } from '@adsk/alloy-react-tooltip';

import BlockRemoveButton from '../../../BlockRemoveButton';
import { capitalizeFirst, colorByMeetingItemStatus } from '../helpers';
import { useEntityLink } from '../helpers/useEntityLink';
import { MeetingItem } from '../types';

type Props = {
  entity: MeetingItem;
  projectId: string;
  onReferenceClickAnalytics?: () => void;
  onRemoveClick?: () => void;
};

const removeButtonClassName = 'MeetingItemEntityBlock__remove-button';

const StyledEntityBlock = styled.div`
  position: relative;
  color: ${theme.colors.charcoal900};
  display: flex;
  padding: 14px 16px 16px 16px;
  flex-direction: column;
  justify-content: center;
  border-bottom: 1px solid ${theme.colors.charcoal100};
  ${theme.typography.bodySmall};
  &:hover .${removeButtonClassName} {
    visibility: visible;
  }
`;

const StyledStatusContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 6px;
`;

const StyledNumTitleContainer = styled.div`
  max-width: 70%;
  display: inline;
`;

export const MeetingItemEntityBlock = ({
  entity,
  projectId,
  onReferenceClickAnalytics,
  onRemoveClick,
}: Props) => {
  const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));

  const meetingItemLink = useEntityLink(projectId, entity);
  const isEntityEditable = !!entity?.permittedActions?.includes('edit');

  return (
    <A
      onClick={onReferenceClickAnalytics}
      href={meetingItemLink}
      target="_blank"
      rel="noopener noreferrer"
    >
      <StyledEntityBlock>
        <OverflowTooltip>
          <span style={theme.typography.bodyMedium}>
            <span style={theme.typography.bodyMediumBold}>
              {`${T.translate('MEETING_ITEM')} ${entity.orderIndex}`}
            </span>
            &nbsp;
            {T.translate('IN_TOPIC_NAME', { name: entity.topic.name })}
          </span>
        </OverflowTooltip>

        <StyledStatusContainer>
          {entity.status && (
            <>
              <Badge
                size={Badge.SIZES.SMALL}
                variant={Badge.VARIANTS.LINE}
                color={colorByMeetingItemStatus[entity.status as string]}
                style={{ padding: 0 }}
              >
                {T.translate((entity.status as string)?.toUpperCase())}
              </Badge>

              <Divider
                variant={Divider.VARIANTS.VERTICAL}
                height={12}
                color={theme.colors.charcoal400}
                style={{ margin: '0 12px' }}
              />
            </>
          )}

          <StyledNumTitleContainer>
            <OverflowTooltip>
              {capitalizeFirst(
                T.translate('IN_MEETING_NUM_TITLE', {
                  num: entity.meeting.numInSeries.toString().padStart(2, '0'),
                  title: entity.meeting.title,
                }) as string,
              )}
            </OverflowTooltip>
          </StyledNumTitleContainer>
        </StyledStatusContainer>

        {isEntityEditable && onRemoveClick && (
          <BlockRemoveButton
            className={removeButtonClassName}
            style={{
              backgroundColor: theme.colors.white,
            }}
            onClick={onRemoveClick}
            tooltipProps={{
              getTargetProps: () => ({
                style: {
                  visibility: 'hidden',
                  position: 'absolute',
                  right: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                },
              }),
            }}
          />
        )}
      </StyledEntityBlock>
    </A>
  );
};
